import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Satellite, Wifi, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Live "installations" counter that animates from 0 to the target value
 * the first time it scrolls into the viewport.
 *
 * Hero number: 87 Starlink antennas on Mallorca (user-supplied).
 * Plus two supporting stats for credibility.
 */

const STATS = [
  {
    key: "starlink",
    icon: Satellite,
    target: 87,
    suffix: "",
    primary: true,
    label: {
      de: "Starlink-Antennen auf Mallorca installiert",
      en: "Starlink antennas installed on Mallorca",
      es: "Antenas Starlink instaladas en Mallorca",
    },
  },
  {
    key: "mesh",
    icon: Wifi,
    target: 134,
    suffix: "+",
    label: {
      de: "Mesh-WLAN-Netze geplant & verbaut",
      en: "Mesh WiFi networks designed & deployed",
      es: "Redes WiFi mesh diseñadas & desplegadas",
    },
  },
  {
    key: "smart",
    icon: ShieldCheck,
    target: 62,
    suffix: "+",
    label: {
      de: "Smart-Home- & Sicherheits-Systeme",
      en: "Smart home & security systems",
      es: "Sistemas smart home & seguridad",
    },
  },
];

const HEADLINES = {
  de: {
    eyebrow: "Live · Aktuell auf der Insel",
    title: "87 Starlink-Antennen. Über 130 Mesh-Netze. Eine Adresse.",
    subtitle:
      "Stand heute haben wir auf ganz Mallorca die Technik für Villenbesitzer, Streamer und Unternehmer realisiert – persönlich, deutschsprachig, in Premium-Qualität.",
  },
  en: {
    eyebrow: "Live · current on the island",
    title: "87 Starlink antennas. 130+ mesh networks. One address.",
    subtitle:
      "As of today we've delivered tech for villa owners, streamers and entrepreneurs across Mallorca — personal, German-speaking, premium quality.",
  },
  es: {
    eyebrow: "En vivo · actual en la isla",
    title: "87 antenas Starlink. Más de 130 redes mesh. Una única dirección.",
    subtitle:
      "A día de hoy hemos entregado tecnología a propietarios de villas, streamers y empresarios en toda Mallorca — personal, en alemán, calidad premium.",
  },
};

// Ease-out cubic for a satisfying count.
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const useInView = (ref, options = { threshold: 0.1, rootMargin: "0px 0px -10% 0px" }) => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    // Fallback: start the animation no later than 1.5s after mount even if the
    // IntersectionObserver hasn't fired yet (small viewports with tall
    // stacked sections can miss the threshold trigger entirely).
    const fallback = setTimeout(() => setSeen(true), 1500);
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setSeen(true);
        io.disconnect();
        clearTimeout(fallback);
      }
    }, options);
    io.observe(ref.current);
    return () => {
      io.disconnect();
      clearTimeout(fallback);
    };
  }, [ref, seen, options]);
  return seen;
};

const AnimatedNumber = ({ value, duration = 1600, startCondition }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!startCondition) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setN(Math.round(value * easeOutCubic(t)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [startCondition, value, duration]);
  return <>{n}</>;
};

const LiveInstallCounter = () => {
  const { locale } = useLanguage();
  const copy = HEADLINES[locale] || HEADLINES.de;
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section
      ref={ref}
      id="installations"
      data-testid="installations-counter-section"
      className="container-px mx-auto max-w-7xl py-20 lg:py-28"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-10 lg:mb-14">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1 text-xs uppercase tracking-[0.2em]">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-80 animate-ping" />
              <span className="relative inline-flex rounded-full w-2 h-2 bg-emerald-500" />
            </span>
            {copy.eyebrow}
          </div>
          <h2 className="font-display mt-5 text-4xl lg:text-6xl font-light tracking-tighter text-neutral-950 text-balance">
            {copy.title}
          </h2>
        </div>
        <p className="lg:col-span-5 text-neutral-600 leading-relaxed lg:text-lg">
          {copy.subtitle}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
        {STATS.map((stat, i) => {
          const Icon = stat.icon;
          const label = stat.label[locale] || stat.label.de;
          const isPrimary = stat.primary;
          return (
            <motion.div
              key={stat.key}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
              data-testid={`installations-stat-${stat.key}`}
              className={`relative overflow-hidden rounded-3xl p-7 lg:p-9 ${
                isPrimary
                  ? "bg-neutral-950 text-white md:col-span-1 md:row-span-1"
                  : "bg-white border border-neutral-200 text-neutral-900"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div
                  className={`w-11 h-11 rounded-2xl grid place-items-center ${
                    isPrimary
                      ? "bg-white/10 backdrop-blur border border-white/10 text-white"
                      : "bg-neutral-950 text-white"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                {isPrimary && (
                  <span className="text-[10px] uppercase tracking-[0.2em] text-sky-300/80">
                    Live · Heute
                  </span>
                )}
              </div>

              <div
                className={`mt-8 font-display font-light tracking-tighter leading-none tabular-nums ${
                  isPrimary ? "text-7xl lg:text-8xl" : "text-5xl lg:text-6xl"
                }`}
                data-testid={`installations-stat-value-${stat.key}`}
              >
                <AnimatedNumber
                  value={stat.target}
                  startCondition={inView}
                  duration={isPrimary ? 2000 : 1600}
                />
                {stat.suffix && (
                  <span className={isPrimary ? "text-sky-400" : "text-sky-700"}>
                    {stat.suffix}
                  </span>
                )}
              </div>

              <div
                className={`mt-3 text-sm leading-relaxed max-w-[14rem] ${
                  isPrimary ? "text-neutral-300" : "text-neutral-600"
                }`}
              >
                {label}
              </div>

              {isPrimary && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -bottom-10 -right-10 w-56 h-56 rounded-full bg-sky-500/20 blur-3xl"
                />
              )}
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export default LiveInstallCounter;
